import {
  DocumentVersionApproverStatus,
  type BaseUser,
  type Device,
  type Document,
} from "@models";
import {
  DocumentStatus,
  RegulatoryTypeEnum,
  type DocStatus,
  type FunctionalGroup,
  type Template,
  type TemplateConfig,
} from "@types";
import type { DocumentsPageItem } from "src/components/Documents/Documents.utils";
import { filterDocumentsPageItemsBySearch } from "src/components/Documents/Documents.utils";
import { getLatestDocumentVersion } from "src/utils/documents";
import { compareAlphabetically, sortDevicesByName } from "src/utils/sorting";

export type FilterOption<T extends string = string> = {
  label: string;
  value: T;
};

export type DocumentsActionFilterValue =
  | "waiting-for-approval"
  | "needs-approvers";

export type DocumentsFilterState = {
  isOrgOnlySelected: boolean;
  selectedActionFilters: DocumentsActionFilterValue[];
  selectedApproverUserIds: string[];
  selectedAuthorUserIds: string[];
  selectedDeviceIds: string[];
  selectedDocumentTypes: RegulatoryTypeEnum[];
  selectedFunctionalGroupIds: string[];
  selectedStatuses: DocStatus[];
};

export const EMPTY_DOCUMENTS_FILTERS: DocumentsFilterState = {
  isOrgOnlySelected: false,
  selectedActionFilters: [],
  selectedApproverUserIds: [],
  selectedAuthorUserIds: [],
  selectedDeviceIds: [],
  selectedDocumentTypes: [],
  selectedFunctionalGroupIds: [],
  selectedStatuses: [],
};

export const documentsActionOptions: FilterOption<DocumentsActionFilterValue>[] =
  [
    {
      label: "Waiting for approval",
      value: "waiting-for-approval",
    },
    {
      label: "Needs approvers",
      value: "needs-approvers",
    },
  ];

export const toggleDocumentsFilterSelection = <T extends string>(
  current: T[],
  value: T
) =>
  current.includes(value)
    ? current.filter((currentValue) => currentValue !== value)
    : [...current, value];

export const hasActiveDocumentsFilters = (filters: DocumentsFilterState) =>
  Object.values(filters).some((value) =>
    Array.isArray(value) ? value.length > 0 : value
  );

export const getSortedDeviceFilterOptions = (
  devices: Device[]
): FilterOption[] =>
  sortDevicesByName(devices).map((device) => ({
    value: device.id,
    label: device.name,
  }));

export const getSelectedDocumentFilterUsers = (
  selectedUserIds: string[],
  users: BaseUser[]
) =>
  selectedUserIds
    .map((userId) => users.find((user) => user.id === userId))
    .filter((user): user is BaseUser => user != null);

export const getFunctionalGroupFilterOptions = (
  functionalGroups: FunctionalGroup[] | undefined
): FilterOption<string>[] =>
  (functionalGroups ?? [])
    .map((functionalGroup) => ({
      value: functionalGroup.id,
      label: `${functionalGroup.name} (${functionalGroup.acronym})`,
    }))
    .sort((left, right) => compareAlphabetically(left.label, right.label));

export const getDocumentTypeFilterOptions =
  (): FilterOption<RegulatoryTypeEnum>[] =>
    Object.values(RegulatoryTypeEnum)
      .map((value) => ({ value, label: value }))
      .sort((left, right) => compareAlphabetically(left.label, right.label));

export const getStatusFilterOptions = (): FilterOption<DocStatus>[] => [
  { value: DocumentStatus.DRAFT, label: "Draft" },
  { value: DocumentStatus.LIVE, label: "Live" },
  { value: DocumentStatus.UNAPPROVED, label: "Unapproved" },
];

export const buildDocumentFilterRecords = ({
  functionalGroups,
  items,
  resolveTemplateForDocument,
  templatesConfig,
}: {
  functionalGroups: FunctionalGroup[] | undefined;
  items: DocumentsPageItem[];
  resolveTemplateForDocument?: (document: Document) => Template | undefined;
  templatesConfig: TemplateConfig | undefined;
}) => {
  const functionalGroupIdsByAcronym = new Map(
    (functionalGroups ?? []).map((group) => [group.acronym, group.id])
  );

  return items.flatMap((item) => {
    if (item.kind !== "document") {
      return [];
    }

    const latestVersion = getLatestDocumentVersion(item.document);

    if (!latestVersion || !item.statusValue) {
      return [];
    }

    const template =
      resolveTemplateForDocument?.(item.document) ??
      (item.document.templateKey
        ? templatesConfig?.[item.document.templateKey]
        : undefined);
    const functionalGroupId =
      item.document.functionalGroupId ??
      (template?.functionalGroupAcronym
        ? (functionalGroupIdsByAcronym.get(template.functionalGroupAcronym) ??
          null)
        : null);
    const isApprovalDraft =
      latestVersion.readyForApproval === true && latestVersion.revision == null;

    return [
      {
        id: item.document.id,
        approverUserIds: latestVersion.documentVersionApprover
          .map((approver: { userId?: string | null }) => approver.userId)
          .filter((userId): userId is string => Boolean(userId)),
        authorUserId: latestVersion.createdBy || null,
        deviceId: item.document.deviceId ?? null,
        functionalGroupId,
        hasPendingApproval:
          isApprovalDraft &&
          latestVersion.documentVersionApprover.some(
            (approver) =>
              approver.approvalStatus !== DocumentVersionApproverStatus.APPROVED
          ),
        needsApproverAssignment:
          isApprovalDraft && latestVersion.documentVersionApprover.length < 2,
        regulatoryType:
          (item.document.regulatoryType as RegulatoryTypeEnum | null) ??
          template?.regulatoryType ??
          null,
        status: item.statusValue,
      },
    ];
  });
};

export type DocumentFilterRecord = ReturnType<
  typeof buildDocumentFilterRecords
>[number];

const matchesSelectedValue = <TValue extends string>(
  selectedValues: TValue[],
  value: TValue | null
) =>
  selectedValues.length === 0 ||
  (value != null && selectedValues.includes(value));

const actionFilterMatches: Record<
  DocumentsActionFilterValue,
  (record: DocumentFilterRecord) => boolean
> = {
  "waiting-for-approval": (record) => record.hasPendingApproval,
  "needs-approvers": (record) => record.needsApproverAssignment,
};

const matchesDocumentsFilters = (
  record: DocumentFilterRecord,
  filters: DocumentsFilterState
) => {
  const matchesScope =
    (filters.selectedDeviceIds.length === 0 && !filters.isOrgOnlySelected) ||
    (filters.isOrgOnlySelected && record.deviceId == null) ||
    (record.deviceId != null &&
      filters.selectedDeviceIds.includes(record.deviceId));

  return (
    matchesScope &&
    matchesSelectedValue(filters.selectedStatuses, record.status) &&
    matchesSelectedValue(
      filters.selectedFunctionalGroupIds,
      record.functionalGroupId
    ) &&
    matchesSelectedValue(
      filters.selectedDocumentTypes,
      record.regulatoryType
    ) &&
    matchesSelectedValue(filters.selectedAuthorUserIds, record.authorUserId) &&
    (filters.selectedApproverUserIds.length === 0 ||
      record.approverUserIds.some((userId) =>
        filters.selectedApproverUserIds.includes(userId)
      )) &&
    (filters.selectedActionFilters.length === 0 ||
      filters.selectedActionFilters.some((actionFilter) =>
        actionFilterMatches[actionFilter](record)
      ))
  );
};

export const filterDocumentsPageItems = ({
  filterRecords,
  filters,
  items,
  search,
}: {
  filterRecords: DocumentFilterRecord[];
  filters: DocumentsFilterState;
  items: DocumentsPageItem[];
  search: string;
}) => {
  const recordsById = new Map(
    filterRecords.map((record) => [record.id, record])
  );
  const filteredItems = hasActiveDocumentsFilters(filters)
    ? items.filter((item) => {
        if (item.kind !== "document") {
          return false;
        }

        const record = recordsById.get(item.id);

        return record ? matchesDocumentsFilters(record, filters) : false;
      })
    : items;

  return filterDocumentsPageItemsBySearch({ items: filteredItems, search });
};
